import { useRef } from "react"
import { UseFormClearErrors, UseFormGetValues, UseFormSetValue } from "react-hook-form"
import {
  AddressSource,
  isPartnerAddressSource,
  TeleconsultationFormInput,
} from "@/modules/patients/agenda/teleconsultation/booking/teleconsultation-schema"

type AddressSnapshot = {
  phoneNumber: string | null
  addressSource: AddressSource | undefined
  postalCode: string | null
  city: string | null
  country: string | null
  houseNumberStreet: string | null
}

type MedgateConnectAction =
  | { type: "enter-partner"; partnerPhone: string | null }
  | { type: "leave-partner"; fallbackPhone: string | null }
  | { type: "leave-medgate-connect"; fallbackPhone: string | null }
  | { type: "partner-cleared" }
  | { type: "partner-selected"; partnerPhone: string }

const ADDRESS_ERROR_FIELDS = [
  "patientPhoneNumber",
  "addressSource",
  "country",
  "city",
  "postalCode",
  "houseNumberStreet",
] as const

const useAddressManager = (
  setValue: UseFormSetValue<TeleconsultationFormInput>,
  getValues: UseFormGetValues<TeleconsultationFormInput>,
  clearErrors: UseFormClearErrors<TeleconsultationFormInput>,
) => {
  const savedPatientAddressRef = useRef<AddressSnapshot | null>(null)

  const clearAddressErrors = () => clearErrors([...ADDRESS_ERROR_FIELDS])

  const snapshot = () => {
    savedPatientAddressRef.current = {
      phoneNumber: getValues("patientPhoneNumber"),
      addressSource: getValues("addressSource"),
      postalCode: getValues("postalCode"),
      city: getValues("city"),
      country: getValues("country"),
      houseNumberStreet: getValues("houseNumberStreet"),
    }
  }

  const restore = (fallbackPhone: string | null) => {
    const saved = savedPatientAddressRef.current
    if (saved && !isPartnerAddressSource(saved.addressSource)) {
      setValue("addressSource", saved.addressSource)
      setValue("patientPhoneNumber", saved.phoneNumber ?? fallbackPhone)
      setValue("postalCode", saved.postalCode ?? null)
      setValue("city", saved.city ?? null)
      setValue("country", saved.country ?? null)
      setValue("houseNumberStreet", saved.houseNumberStreet ?? null)
    } else {
      reset(saved?.phoneNumber ?? fallbackPhone)
    }
  }

  const reset = (fallbackPhone: string | null) => {
    setValue("addressSource", undefined)
    setValue("patientPhoneNumber", fallbackPhone)
    setValue("postalCode", null)
    setValue("city", null)
    setValue("country", null)
    setValue("houseNumberStreet", null)
  }

  const processAddressChange = (action: MedgateConnectAction) => {
    switch (action.type) {
      case "enter-partner":
        if (getValues("addressSource") !== "PARTNER") {
          snapshot()
        }
        setValue("addressSource", "PARTNER")
        setValue("patientPhoneNumber", action.partnerPhone)
        break
      case "leave-partner": {
        if (getValues("addressSource") !== "PARTNER") break
        const saved = savedPatientAddressRef.current
        if (saved) {
          setValue("addressSource", saved.addressSource)
          setValue("patientPhoneNumber", saved.phoneNumber ?? action.fallbackPhone)
          setValue("postalCode", saved.postalCode ?? null)
          setValue("city", saved.city ?? null)
          setValue("country", saved.country ?? null)
          setValue("houseNumberStreet", saved.houseNumberStreet ?? null)
        } else {
          reset(action.fallbackPhone)
        }
        break
      }
      case "leave-medgate-connect": {
        const source = getValues("addressSource")
        if (source === "PARTNER") {
          restore(action.fallbackPhone)
        } else if (source === "PARTNER_LOCATION") {
          setValue("addressSource", undefined)
        }
        break
      }
      case "partner-cleared": {
        const source = getValues("addressSource")
        if (source === "PARTNER") {
          setValue("patientPhoneNumber", null)
        } else if (source === "PARTNER_LOCATION") {
          setValue("addressSource", undefined)
        }
        break
      }
      case "partner-selected":
        if (getValues("addressSource") === "PARTNER") {
          setValue("patientPhoneNumber", action.partnerPhone)
        }
        break
    }
    clearAddressErrors()
  }

  return { processAddressChange }
}

export { useAddressManager }
